import type { ThirdPartyReviewSource } from './types'

const TAB_ORDER: ThirdPartyReviewSource[] = ['google', 'yelp', 'houzz']

/**
 * Unwraps Payload select `value` / `label` and a few other shapes so we always have text to match on.
 * Prefer `value`; if empty, use `label` (e.g. admin only stored label, or import quirks).
 */
export function getThirdPartySourceRawString(raw: unknown): string {
  if (raw == null) {
    return ''
  }

  if (typeof raw === 'string') {
    return raw
  }

  if (typeof raw === 'number' && !Number.isNaN(raw) && raw >= 0 && raw < TAB_ORDER.length) {
    // If something serializes the enum as 0 / 1 / 2 (order: google, yelp, houzz)
    return TAB_ORDER[raw] ?? String(raw)
  }

  if (typeof raw === 'object' && raw !== null) {
    const o = raw as Record<string, unknown>
    if (o.value != null) {
      if (typeof o.value === 'string' && o.value.length > 0) {
        return o.value
      }
      if (typeof o.value === 'number' && !Number.isNaN(o.value) && o.value >= 0 && o.value < TAB_ORDER.length) {
        return TAB_ORDER[o.value] ?? String(o.value)
      }
    }
    if (typeof o.label === 'string' && o.label.length > 0) {
      return o.label
    }
  }

  return String(raw)
}

function foldBrandCharsToAscii(s: string): string {
  // Greek / Cyrillic / fullwidth o so "Houzz" with a non-ASCII o still normalizes
  return s
    .replace(/\u03bf/g, 'o') // omicron
    .replace(/\u043e/g, 'o') // Cyrillic small o
    .replace(/[\uFF21-\uFF3A\uFF41-\uFF5A]/g, (c) => {
      const u = c.codePointAt(0) ?? 0
      if (u >= 0xff41 && u <= 0xff5a) {
        return String.fromCodePoint(u - 0xff41 + 0x61)
      }
      if (u >= 0xff21 && u <= 0xff3a) {
        return String.fromCodePoint(u - 0xff21 + 0x41)
      }
      return c
    })
}

type Normalized = ThirdPartyReviewSource

/**
 * Map CMS / DB / paste values to `google` | `yelp` | `houzz`. Unrecognized input falls back to `google`.
 */
export function normalizeThirdPartySource(raw: unknown): Normalized {
  let rawStr = getThirdPartySourceRawString(raw)
  if (rawStr.trim() !== '' && /^\d+$/.test(rawStr.trim())) {
    const n = Number.parseInt(rawStr, 10)
    if (n >= 0 && n < TAB_ORDER.length) {
      return TAB_ORDER[n] ?? 'google'
    }
  }

  rawStr = foldBrandCharsToAscii(rawStr)
  const s = rawStr.normalize('NFKC').trim().toLowerCase()
  if (!s) {
    return 'google'
  }

  const letters = s.replace(/[^a-z]/g, '')

  // Known alias when the whole value is a shorthand (avoids "amazon" / random "hz" matches)
  if (s === 'hz' || letters === 'hz' || s === 'hz-') {
    return 'houzz'
  }

  if (s.includes('yelp') || letters === 'yelp' || (letters.length >= 3 && letters.includes('yelp'))) {
    return 'yelp'
  }

  if (
    s.includes('houzz') ||
    s.includes('houz') ||
    letters.includes('houzz') ||
    letters.startsWith('houz') ||
    s.includes('houz_') ||
    s.includes('houz-') ||
    (letters.length >= 4 && letters.length <= 30 && letters.includes('houz'))
  ) {
    return 'houzz'
  }

  if (s.includes('google') || letters.includes('google') || s.includes('googl')) {
    return 'google'
  }

  return 'google'
}
